import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Checkbox,
  Divider,
  Drawer,
  Group,
  Loader,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import {
  IconAlertTriangle,
  IconPlus,
  IconSignature,
  IconTrash,
  IconWriting,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { ApiError} from '../../api/client';
import { api, unwrap } from '../../api/client';
import type { FlightRecord } from '../../api/types';
import { useSession } from '../../app/session';
import { AttachmentList } from '../../components/Attachments';
import { DurationInput, formatDuration } from '../../components/DurationInput';
import { StatusBadge } from '../../components/StatusBadge';
import { formatCounter, fromApiDate, toApiDate, today } from '../../lib/format';
import { applyFieldErrors, notifyError, notifyInfo, notifySaved } from '../../lib/notify';

type CrewRow = {
  key: string;
  id?: number;
  person: string;
  function: string;
  time_logged_minutes: number | null;
  is_under_training: boolean;
  seat: string;
  remarks: string;
  countersigned_by_name?: string | null;
};

type FormValues = {
  date: Date | null;
  aircraft: string;
  departure_place: string;
  departure_time: string;
  arrival_place: string;
  arrival_time: string;
  total_time_minutes: number | null;
  night_minutes: number | null;
  ifr_minutes: number | null;
  instrument_minutes: number | null;
  cross_country_minutes: number | null;
  multi_pilot_minutes: number | null;
  landings_day: number | '';
  landings_night: number | '';
  purpose: string;
  school: string;
  is_paid: boolean;
  launch_method: string;
  launches: number | '';
  tow_aircraft: string;
  tow_pilot: string;
  max_altitude_m: number | '';
  distance_km: number | '';
  motor_minutes: number | null;
  gliding_minutes: number | null;
  heating_type: string;
  heater_minutes: number | null;
  remarks: string;
  crew: CrewRow[];
};

const EMPTY_CREW: Omit<CrewRow, 'key'> = {
  person: '',
  function: 'PIC',
  time_logged_minutes: null,
  is_under_training: false,
  seat: '',
  remarks: '',
};

let crewKeySeed = 0;
const newCrewKey = () => `crew-${++crewKeySeed}`;

/**
 * Filing and reading one flight.
 *
 * A certified entry is read-only by design: the drawer switches to showing the entry plus a
 * correction button, because a Part-FCL logbook is corrected by superseding an entry, never
 * by editing it in place.
 */
export function FlightDrawer({
  opened,
  onClose,
  flightId,
  defaultAircraftId,
  defaultPersonId,
}: {
  opened: boolean;
  onClose: () => void;
  flightId: number | null;
  defaultAircraftId?: number;
  defaultPersonId?: number;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { can, enumChoices } = useSession();
  const [isCorrecting, setCorrecting] = useState(false);
  const [correctionReason, setCorrectionReason] = useState('');

  const flight = useQuery({
    queryKey: ['flight', flightId],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/flights/{id}/', { params: { path: { id: flightId as number } } }),
      ),
    enabled: opened && flightId !== null,
  });

  const aircraftList = useQuery({
    queryKey: ['aircraft', 'options'],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/aircraft/', { params: { query: { page_size: 200 } } }),
      ),
    enabled: opened,
    staleTime: 5 * 60 * 1000,
  });

  const people = useQuery({
    queryKey: ['people', 'options'],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/people/', { params: { query: { page_size: 300 } } })),
    enabled: opened,
    staleTime: 5 * 60 * 1000,
  });

  const schools = useQuery({
    queryKey: ['organisations', 'schools'],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/organisations/', {
          params: { query: { kind: 'FLIGHT_SCHOOL', page_size: 50 } },
        }),
      ),
    enabled: opened,
    staleTime: 5 * 60 * 1000,
  });

  const form = useForm<FormValues>({
    initialValues: blankValues(defaultAircraftId),
    validate: {
      date: (value) => (value ? null : t('common.requiredField')),
      aircraft: (value) => (value ? null : t('common.requiredField')),
      departure_place: (value) => (value.trim() ? null : t('common.requiredField')),
      arrival_place: (value) => (value.trim() ? null : t('common.requiredField')),
      total_time_minutes: (value) => (value && value > 0 ? null : t('common.requiredField')),
      crew: {
        person: (value) => (value ? null : t('common.requiredField')),
      },
    },
  });

  const record = flight.data;
  const isNew = flightId === null;
  const isReadOnly = Boolean(record && !record.is_editable) && !isCorrecting;

  useEffect(() => {
    if (!opened) return;
    setCorrecting(false);
    setCorrectionReason('');
    if (isNew) {
      form.setValues(blankValues(defaultAircraftId, defaultPersonId));
      form.resetDirty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, flightId]);

  useEffect(() => {
    if (!record) return;
    form.setValues(toFormValues(record));
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id, record?.state]);

  const selectedAircraft = useMemo(
    () =>
      (aircraftList.data?.results ?? []).find(
        (item) => String(item.id) === form.values.aircraft,
      ),
    [aircraftList.data, form.values.aircraft],
  );

  const category = selectedAircraft?.category ?? record?.category;
  const isGlider = category === 'GLIDER';
  const isTmg = category === 'TMG';
  const isBalloon = category === 'BALLOON';
  const needsLaunch = isGlider || isTmg;

  // Block time is arithmetic, so it is filled in for the filer rather than asked for twice.
  useEffect(() => {
    const { departure_time, arrival_time, total_time_minutes } = form.values;
    if (!departure_time || !arrival_time || total_time_minutes) return;
    const minutes = blockMinutes(departure_time, arrival_time);
    if (minutes) form.setFieldValue('total_time_minutes', minutes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.departure_time, form.values.arrival_time]);

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const body = toApiBody(values);
      if (isCorrecting && record) {
        return unwrap(
          await api.POST('/api/v1/flights/{id}/correct/', {
            params: { path: { id: record.id } },
            body: { reason: correctionReason, changes: body },
          }),
        );
      }
      if (record) {
        return unwrap(
          await api.PATCH('/api/v1/flights/{id}/', {
            params: { path: { id: record.id } },
            body,
          }),
        );
      }
      return unwrap(await api.POST('/api/v1/flights/', { body }));
    },
    onSuccess: async () => {
      notifySaved();
      await invalidate();
      onClose();
    },
    onError: (error: ApiError) => {
      if (!applyFieldErrors(error, form.setErrors)) notifyError(error);
    },
  });

  const certify = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/api/v1/flights/{id}/certify/', {
          params: { path: { id: record!.id } },
          body: {},
        }),
      ),
    onSuccess: async () => {
      notifyInfo(t('logbook.CERTIFIED'));
      await invalidate();
    },
    onError: notifyError,
  });

  const cancelFlight = useMutation({
    mutationFn: async (reason: string) =>
      unwrap(
        await api.POST('/api/v1/flights/{id}/cancel/', {
          params: { path: { id: record!.id } },
          body: { reason },
        }),
      ),
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
    onError: notifyError,
  });

  const countersign = useMutation({
    mutationFn: async (crewId: number) =>
      unwrap(
        await api.POST('/api/v1/flight-crew/{id}/countersign/', {
          params: { path: { id: crewId } },
          body: {},
        }),
      ),
    onSuccess: async () => {
      await invalidate();
    },
    onError: notifyError,
  });

  async function invalidate() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['logbook'] }),
      queryClient.invalidateQueries({ queryKey: ['flight'] }),
      queryClient.invalidateQueries({ queryKey: ['aircraft'] }),
      queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      queryClient.invalidateQueries({ queryKey: ['maintenance'] }),
    ]);
  }

  const confirmCancel = () =>
    modals.open({
      title: t('logbook.cancelReason'),
      children: <CancelForm onSubmit={(reason) => cancelFlight.mutate(reason)} />,
    });

  const aircraftOptions = (aircraftList.data?.results ?? [])
    .filter((item) => item.status !== 'SOLD')
    .map((item) => ({
      value: String(item.id),
      label: `${item.registration} · ${item.type_label}`,
    }));

  const peopleOptions = (people.data?.results ?? []).map((person) => ({
    value: String(person.id),
    label: person.full_name,
  }));

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      title={
        <Group gap="sm">
          <Text fw={600}>
            {isNew
              ? t('logbook.fileFlight')
              : isCorrecting
                ? t('action.correct')
                : t('logbook.editFlight')}
          </Text>
          {record ? <StatusBadge kind="flight" value={record.state} size="sm" /> : null}
          {record?.is_cancelled ? (
            <Badge color="gray" size="sm">
              {t('action.cancel')}
            </Badge>
          ) : null}
        </Group>
      }
    >
      {flight.isLoading ? (
        <Loader />
      ) : (
        <form onSubmit={form.onSubmit((values) => save.mutate(values))}>
          <Stack gap="lg">
            {isReadOnly ? (
              <Alert color="teal" icon={<IconSignature size={17} />} variant="light">
                {t('logbook.certifyConfirm')}
              </Alert>
            ) : null}

            {isCorrecting ? (
              <TextInput
                label={t('logbook.correctionReason')}
                required
                value={correctionReason}
                onChange={(event) => setCorrectionReason(event.currentTarget.value)}
              />
            ) : null}

            <Group grow align="flex-start">
              <DatePickerInput
                label={t('logbook.date')}
                required
                valueFormat="DD.MM.YYYY"
                maxDate={new Date()}
                disabled={isReadOnly}
                {...form.getInputProps('date')}
              />
              <Select
                label={t('logbook.aircraft')}
                required
                data={aircraftOptions}
                searchable
                allowDeselect={false}
                disabled={isReadOnly}
                {...form.getInputProps('aircraft')}
              />
            </Group>

            <Group grow align="flex-start">
              <TextInput
                label={t('logbook.departurePlace')}
                required
                placeholder="LYNK"
                disabled={isReadOnly}
                {...form.getInputProps('departure_place')}
                onChange={(event) =>
                  form.setFieldValue('departure_place', event.currentTarget.value.toUpperCase())
                }
              />
              <TimeField
                label={t('logbook.departureTime')}
                disabled={isReadOnly}
                {...form.getInputProps('departure_time')}
              />
              <TextInput
                label={t('logbook.arrivalPlace')}
                required
                placeholder="LYNK"
                disabled={isReadOnly}
                {...form.getInputProps('arrival_place')}
                onChange={(event) =>
                  form.setFieldValue('arrival_place', event.currentTarget.value.toUpperCase())
                }
              />
              <TimeField
                label={t('logbook.arrivalTime')}
                disabled={isReadOnly}
                {...form.getInputProps('arrival_time')}
              />
            </Group>

            <Group grow align="flex-start">
              <DurationInput
                label={t('logbook.totalTime')}
                required
                disabled={isReadOnly}
                value={form.values.total_time_minutes}
                onChange={(minutes) => form.setFieldValue('total_time_minutes', minutes)}
                error={form.errors.total_time_minutes}
              />
              {!isGlider ? (
                <DurationInput
                  label={t('logbook.night')}
                  disabled={isReadOnly}
                  value={form.values.night_minutes}
                  onChange={(minutes) => form.setFieldValue('night_minutes', minutes)}
                  error={form.errors.night_minutes}
                />
              ) : null}
              {!isGlider && !isBalloon ? (
                <DurationInput
                  label={t('logbook.ifr')}
                  disabled={isReadOnly}
                  value={form.values.ifr_minutes}
                  onChange={(minutes) => form.setFieldValue('ifr_minutes', minutes)}
                  error={form.errors.ifr_minutes}
                />
              ) : null}
              <DurationInput
                label={t('logbook.crossCountry')}
                disabled={isReadOnly}
                value={form.values.cross_country_minutes}
                onChange={(minutes) => form.setFieldValue('cross_country_minutes', minutes)}
              />
            </Group>

            {selectedAircraft?.seats && selectedAircraft.seats > 1 ? (
              <Group grow align="flex-start">
                <DurationInput
                  label={t('logbook.instrument')}
                  disabled={isReadOnly}
                  value={form.values.instrument_minutes}
                  onChange={(minutes) => form.setFieldValue('instrument_minutes', minutes)}
                />
                <DurationInput
                  label={t('logbook.multiPilot')}
                  disabled={isReadOnly}
                  value={form.values.multi_pilot_minutes}
                  onChange={(minutes) => form.setFieldValue('multi_pilot_minutes', minutes)}
                  error={form.errors.multi_pilot_minutes}
                />
              </Group>
            ) : null}

            <Group grow align="flex-start">
              <NumberInput
                label={t('logbook.landingsDay')}
                min={0}
                max={99}
                disabled={isReadOnly}
                {...form.getInputProps('landings_day')}
              />
              <NumberInput
                label={t('logbook.landingsNight')}
                min={0}
                max={99}
                disabled={isReadOnly}
                {...form.getInputProps('landings_night')}
              />
            </Group>

            {needsLaunch ? (
              <>
                <Divider label={t('logbook.launchMethod')} labelPosition="left" />
                <Group grow align="flex-start">
                  <Select
                    label={t('logbook.launchMethod')}
                    required
                    data={enumChoices('launch_method').map((choice) => ({
                      value: choice.value,
                      label: choice.label,
                    }))}
                    allowDeselect={false}
                    disabled={isReadOnly}
                    {...form.getInputProps('launch_method')}
                  />
                  <NumberInput
                    label={t('logbook.launches')}
                    min={1}
                    max={99}
                    disabled={isReadOnly}
                    {...form.getInputProps('launches')}
                  />
                </Group>
                {form.values.launch_method === 'AEROTOW' ? (
                  <Group grow align="flex-start">
                    <Select
                      label={t('logbook.tug')}
                      data={aircraftOptions}
                      searchable
                      clearable
                      disabled={isReadOnly}
                      {...form.getInputProps('tow_aircraft')}
                    />
                    <Select
                      label={t('logbook.tugPilot')}
                      data={peopleOptions}
                      searchable
                      clearable
                      disabled={isReadOnly}
                      {...form.getInputProps('tow_pilot')}
                    />
                  </Group>
                ) : null}
                <Group grow align="flex-start">
                  <NumberInput
                    label={t('logbook.maxAltitude')}
                    min={0}
                    max={20000}
                    disabled={isReadOnly}
                    {...form.getInputProps('max_altitude_m')}
                  />
                  <NumberInput
                    label={t('logbook.distance')}
                    min={0}
                    decimalScale={1}
                    disabled={isReadOnly}
                    {...form.getInputProps('distance_km')}
                  />
                </Group>
              </>
            ) : null}

            {isTmg ? (
              <Group grow align="flex-start">
                <DurationInput
                  label={t('logbook.motorTime')}
                  description={t('logbook.tmgSplitHint')}
                  disabled={isReadOnly}
                  value={form.values.motor_minutes}
                  onChange={(minutes) => {
                    form.setFieldValue('motor_minutes', minutes);
                    const total = form.values.total_time_minutes;
                    if (total && minutes !== null) {
                      form.setFieldValue('gliding_minutes', Math.max(0, total - minutes));
                    }
                  }}
                  error={form.errors.motor_minutes}
                />
                <DurationInput
                  label={t('logbook.glidingTime')}
                  disabled={isReadOnly}
                  value={form.values.gliding_minutes}
                  onChange={(minutes) => form.setFieldValue('gliding_minutes', minutes)}
                  error={form.errors.gliding_minutes}
                />
              </Group>
            ) : null}

            {isBalloon ? (
              <Group grow align="flex-start">
                <Select
                  label={t('logbook.heatingType')}
                  required
                  data={enumChoices('balloon_heating').map((choice) => ({
                    value: choice.value,
                    label: choice.label,
                  }))}
                  allowDeselect={false}
                  disabled={isReadOnly}
                  {...form.getInputProps('heating_type')}
                />
                <DurationInput
                  label={t('logbook.heaterTime')}
                  disabled={isReadOnly}
                  value={form.values.heater_minutes}
                  onChange={(minutes) => form.setFieldValue('heater_minutes', minutes)}
                />
              </Group>
            ) : null}

            <Divider label={t('logbook.purpose')} labelPosition="left" />

            <Group grow align="flex-start">
              <Select
                label={t('logbook.purpose')}
                data={enumChoices('flight_purpose').map((choice) => ({
                  value: choice.value,
                  label: choice.label,
                }))}
                allowDeselect={false}
                disabled={isReadOnly}
                {...form.getInputProps('purpose')}
              />
              <Select
                label={t('logbook.school')}
                data={(schools.data?.results ?? []).map((school) => ({
                  value: String(school.id),
                  label: school.name,
                }))}
                clearable
                disabled={isReadOnly}
                {...form.getInputProps('school')}
              />
            </Group>

            <Checkbox
              label={t('logbook.isPaid')}
              disabled={isReadOnly}
              {...form.getInputProps('is_paid', { type: 'checkbox' })}
            />

            <Divider label={t('logbook.crew')} labelPosition="left" />

            <Text fz="xs" c="dimmed">
              {t('logbook.picUppercaseHint')}
            </Text>

            <Stack gap="xs">
              {form.values.crew.map((row, index) => (
                <Group key={row.key} gap="xs" align="flex-start" wrap="nowrap">
                  <Select
                    placeholder={t('staff.person')}
                    data={peopleOptions}
                    searchable
                    allowDeselect={false}
                    disabled={isReadOnly}
                    style={{ flex: 1.6 }}
                    {...form.getInputProps(`crew.${index}.person`)}
                  />
                  <Select
                    data={enumChoices('crew_function').map((choice) => ({
                      value: choice.value,
                      label: choice.label,
                    }))}
                    allowDeselect={false}
                    disabled={isReadOnly}
                    style={{ flex: 1.2 }}
                    {...form.getInputProps(`crew.${index}.function`)}
                  />
                  <DurationInput
                    placeholder={formatDuration(form.values.total_time_minutes)}
                    disabled={isReadOnly || isNonLogging(row.function)}
                    value={row.time_logged_minutes}
                    onChange={(minutes) =>
                      form.setFieldValue(`crew.${index}.time_logged_minutes`, minutes)
                    }
                    w={100}
                  />
                  <Tooltip label={t('logbook.underTraining')} withArrow>
                    <Checkbox
                      mt={8}
                      disabled={isReadOnly}
                      {...form.getInputProps(`crew.${index}.is_under_training`, {
                        type: 'checkbox',
                      })}
                    />
                  </Tooltip>
                  {row.id && needsCountersign(row.function) ? (
                    row.countersigned_by_name ? (
                      <Tooltip label={row.countersigned_by_name} withArrow>
                        <IconSignature size={18} style={{ marginTop: 8, opacity: 0.5 }} />
                      </Tooltip>
                    ) : (
                      <Tooltip label={t('logbook.countersign')} withArrow>
                        <ActionIcon
                          variant="light"
                          mt={4}
                          loading={countersign.isPending}
                          onClick={() => countersign.mutate(row.id as number)}
                        >
                          <IconWriting size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )
                  ) : null}
                  {!isReadOnly ? (
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      mt={4}
                      onClick={() => form.removeListItem('crew', index)}
                      aria-label={t('action.delete')}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  ) : null}
                </Group>
              ))}
              {!isReadOnly ? (
                <Button
                  variant="light"
                  size="compact-sm"
                  leftSection={<IconPlus size={14} />}
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() =>
                    form.insertListItem('crew', { ...EMPTY_CREW, key: newCrewKey() })
                  }
                >
                  {t('logbook.addCrew')}
                </Button>
              ) : null}
              {typeof form.errors.crew === 'string' ? (
                <Text fz="xs" c="red">
                  {form.errors.crew}
                </Text>
              ) : null}
            </Stack>

            <Textarea
              label={t('common.remarks')}
              minRows={2}
              autosize
              disabled={isReadOnly}
              {...form.getInputProps('remarks')}
            />

            {record?.counter_entries?.length ? (
              <>
                <Divider label={t('logbook.counterMovements')} labelPosition="left" />
                <Table fz="sm" withRowBorders={false}>
                  <Table.Tbody>
                    {record.counter_entries.map((entry) => (
                      <Table.Tr key={entry.id}>
                        <Table.Td>{entry.counter_label}</Table.Td>
                        <Table.Td ff="monospace" ta="right">
                          {formatCounter(entry.value_before, entry.unit)}
                        </Table.Td>
                        <Table.Td ta="center" c="dimmed">
                          →
                        </Table.Td>
                        <Table.Td ff="monospace" ta="right" fw={600}>
                          {formatCounter(entry.value_after, entry.unit)}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </>
            ) : null}

            {record ? (
              <>
                <Divider label={t('maintenance.attachments')} labelPosition="left" />
                <AttachmentList
                  target="logbook.flightrecord"
                  targetId={record.id}
                  canEdit={!isReadOnly}
                  compact
                />
              </>
            ) : null}

            {record?.corrects ? (
              <Alert color="grape" variant="light" icon={<IconAlertTriangle size={17} />}>
                {t('logbook.correctionReason')}: {record.correction_reason}
              </Alert>
            ) : null}

            <Group justify="space-between" mt="sm">
              <Group gap="xs">
                {record && !isReadOnly && !record.is_cancelled && can('logbook.certify') ? (
                  <Button
                    variant="light"
                    color="teal"
                    leftSection={<IconSignature size={16} />}
                    loading={certify.isPending}
                    onClick={() => certify.mutate()}
                  >
                    {t('action.certify')}
                  </Button>
                ) : null}
                {record && isReadOnly && can('logbook.correct') ? (
                  <Button variant="light" color="grape" onClick={() => setCorrecting(true)}>
                    {t('action.correct')}
                  </Button>
                ) : null}
                {record && !record.is_cancelled && can('logbook.certify') ? (
                  <Button variant="subtle" color="red" onClick={confirmCancel}>
                    {t('action.cancel')}
                  </Button>
                ) : null}
              </Group>
              <Group gap="xs">
                <Button variant="default" onClick={onClose}>
                  {t('action.close')}
                </Button>
                {!isReadOnly ? (
                  <Button
                    type="submit"
                    loading={save.isPending}
                    disabled={isCorrecting && !correctionReason.trim()}
                  >
                    {t('action.save')}
                  </Button>
                ) : null}
              </Group>
            </Group>

            {record?.certified_by_name ? (
              <Text fz="xs" c="dimmed" ta="right">
                {t('action.certify')}: {record.certified_by_name}
              </Text>
            ) : null}
          </Stack>
        </form>
      )}
    </Drawer>
  );

  function isNonLogging(fn: string) {
    return fn === 'PASSENGER' || fn === 'OCCUPANT' || fn === 'STUDENT';
  }

  function needsCountersign(fn: string) {
    return fn === 'SPIC' || fn === 'PICUS';
  }

  function toApiBody(values: FormValues) {
    return {
      date: toApiDate(values.date) as string,
      aircraft: Number(values.aircraft),
      departure_place: values.departure_place.trim(),
      departure_time: values.departure_time || null,
      arrival_place: values.arrival_place.trim(),
      arrival_time: values.arrival_time || null,
      total_time: formatDuration(values.total_time_minutes),
      night_time: formatDuration(values.night_minutes ?? 0),
      ifr_time: formatDuration(values.ifr_minutes ?? 0),
      instrument_time: formatDuration(values.instrument_minutes ?? 0),
      cross_country_time: formatDuration(values.cross_country_minutes ?? 0),
      multi_pilot_time: formatDuration(values.multi_pilot_minutes ?? 0),
      landings_day: values.landings_day === '' ? 0 : values.landings_day,
      landings_night: values.landings_night === '' ? 0 : values.landings_night,
      purpose: values.purpose as FlightRecord['purpose'],
      school: values.school ? Number(values.school) : null,
      is_paid: values.is_paid,
      launch_method: needsLaunch
        ? (values.launch_method as FlightRecord['launch_method'])
        : ('' as FlightRecord['launch_method']),
      launches: needsLaunch ? (values.launches === '' ? 1 : values.launches) : 0,
      tow_aircraft: values.tow_aircraft ? Number(values.tow_aircraft) : null,
      tow_pilot: values.tow_pilot ? Number(values.tow_pilot) : null,
      max_altitude_m: values.max_altitude_m === '' ? null : values.max_altitude_m,
      distance_km: values.distance_km === '' ? null : String(values.distance_km),
      motor_time: formatDuration(isTmg ? (values.motor_minutes ?? 0) : 0),
      gliding_time: formatDuration(isTmg ? (values.gliding_minutes ?? 0) : 0),
      heating_type: isBalloon
        ? (values.heating_type as FlightRecord['heating_type'])
        : ('' as FlightRecord['heating_type']),
      heater_time: formatDuration(isBalloon ? (values.heater_minutes ?? 0) : 0),
      remarks: values.remarks,
      crew: values.crew
        .filter((row) => row.person)
        .map((row) => ({
          id: row.id,
          person: Number(row.person),
          function: row.function as NonNullable<FlightRecord['crew']>[number]['function'],
          time_logged: formatDuration(row.time_logged_minutes ?? values.total_time_minutes),
          is_under_training: row.is_under_training,
          seat: row.seat,
          remarks: row.remarks,
        })),
    };
  }
}

function blankValues(aircraftId?: number, personId?: number): FormValues {
  return {
    date: fromApiDate(today()),
    aircraft: aircraftId ? String(aircraftId) : '',
    departure_place: '',
    departure_time: '',
    arrival_place: '',
    arrival_time: '',
    total_time_minutes: null,
    night_minutes: 0,
    ifr_minutes: 0,
    instrument_minutes: 0,
    cross_country_minutes: 0,
    multi_pilot_minutes: 0,
    landings_day: 1,
    landings_night: 0,
    purpose: 'PRIVATE',
    school: '',
    is_paid: true,
    launch_method: '',
    launches: 1,
    tow_aircraft: '',
    tow_pilot: '',
    max_altitude_m: '',
    distance_km: '',
    motor_minutes: 0,
    gliding_minutes: 0,
    heating_type: '',
    heater_minutes: 0,
    remarks: '',
    crew: [
      {
        ...EMPTY_CREW,
        key: newCrewKey(),
        person: personId ? String(personId) : '',
      },
    ],
  };
}

function toFormValues(record: FlightRecord): FormValues {
  return {
    date: fromApiDate(record.date),
    aircraft: String(record.aircraft),
    departure_place: record.departure_place,
    departure_time: (record.departure_time ?? '').slice(0, 5),
    arrival_place: record.arrival_place,
    arrival_time: (record.arrival_time ?? '').slice(0, 5),
    total_time_minutes: record.total_time_minutes,
    night_minutes: toMinutes(record.night_time),
    ifr_minutes: toMinutes(record.ifr_time),
    instrument_minutes: toMinutes(record.instrument_time),
    cross_country_minutes: toMinutes(record.cross_country_time),
    multi_pilot_minutes: toMinutes(record.multi_pilot_time),
    landings_day: record.landings_day ?? 0,
    landings_night: record.landings_night ?? 0,
    purpose: record.purpose ?? 'PRIVATE',
    school: record.school ? String(record.school) : '',
    is_paid: record.is_paid ?? true,
    launch_method: record.launch_method ?? '',
    launches: record.launches ?? 0,
    tow_aircraft: record.tow_aircraft ? String(record.tow_aircraft) : '',
    tow_pilot: record.tow_pilot ? String(record.tow_pilot) : '',
    max_altitude_m: record.max_altitude_m ?? '',
    distance_km: record.distance_km ? Number(record.distance_km) : '',
    motor_minutes: toMinutes(record.motor_time),
    gliding_minutes: toMinutes(record.gliding_time),
    heating_type: record.heating_type ?? '',
    heater_minutes: toMinutes(record.heater_time),
    remarks: record.remarks ?? '',
    crew: (record.crew ?? []).map((row) => ({
      key: newCrewKey(),
      id: row.id,
      person: String(row.person),
      function: row.function,
      time_logged_minutes: row.time_logged_minutes ?? 0,
      is_under_training: row.is_under_training ?? false,
      seat: row.seat ?? '',
      remarks: row.remarks ?? '',
      countersigned_by_name: row.countersigned_by_name,
    })),
  };
}

/** The API speaks "H:MM"; the form holds minutes so it can do arithmetic on them. */
function toMinutes(value?: string | null) {
  if (!value) return 0;
  const [hours, minutes] = value.split(':');
  return Number(hours || 0) * 60 + Number(minutes || 0);
}

/** Block time, wrapping past midnight the way a late arrival actually does. */
function blockMinutes(out: string, back: string) {
  const start = toMinutes(out);
  const end = toMinutes(back);
  if (!out || !back) return 0;
  return end >= start ? end - start : 24 * 60 - start + end;
}

function TimeField(props: React.ComponentProps<typeof TextInput>) {
  return (
    <TextInput
      placeholder="HH:MM"
      maxLength={5}
      {...props}
      styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
    />
  );
}

function CancelForm({ onSubmit }: { onSubmit: (reason: string) => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  return (
    <Stack gap="sm">
      <Title order={6}>{t('logbook.cancelReason')}</Title>
      <Textarea
        autosize
        minRows={2}
        value={reason}
        onChange={(event) => setReason(event.currentTarget.value)}
      />
      <Group justify="flex-end">
        <Button variant="default" onClick={() => modals.closeAll()}>
          {t('action.cancel')}
        </Button>
        <Button
          color="red"
          disabled={!reason.trim()}
          onClick={() => {
            onSubmit(reason.trim());
            modals.closeAll();
          }}
        >
          {t('action.delete')}
        </Button>
      </Group>
    </Stack>
  );
}
